import { NextApiRequest } from 'next';
import { NextRequest } from 'next/server';
import { IncomingMessage } from 'node:http';

import { EMPTY_STR } from './constants/common';
import { SEARCH_ENGINE_CRAWLERS } from './constants/searchEngineCrawlers';
import { getHeaderValue } from './server/request-extractor';
import { isMobilePhoneUserAgent } from './user-agent/isMobilePhoneUserAgent';

function isUserAgentBot(ua: string): boolean {
    // copied condition from legacy codebase
    if (ua.toLowerCase().includes('google')) {
        return true;
    }

    return Boolean(ua.match(new RegExp(SEARCH_ENGINE_CRAWLERS.join('|'), 'i'))?.length);
}

/**
 * @req NextAPIRequest from an api call
 * returns the web host in a paramount.com format
 */

function getWebHost(req: NextApiRequest) {
    const { headers } = req;
    if (headers) {
        // Behind the shell's proxy `host` is the MFE's own (e.g. localhost:3009); the browser-facing
        // host arrives as x-forwarded-host.
        const forwardedHost = headers['x-forwarded-host']?.toString().split(',')[0].trim();
        const host = (forwardedHost || headers.host)?.split(':')[0];
        if (host) {
            const splitHost = host.split('.');
            const splitLen = splitHost.length;
            return splitLen > 1 ? `${splitHost[splitLen - 2]}.${splitHost[splitLen - 1]}` : host;
        }
    }
    return EMPTY_STR;
}

/**
 * @req NextAPIRequest from an api call
 * Grabs the user agents from either the network request, or the client window. It prefers grabbing from the client
 */
function getUserAgent(req?: NextApiRequest) {
    if (typeof window !== 'undefined') {
        const { navigator } = window;
        if (typeof navigator !== 'undefined') {
            return navigator.userAgent;
        }
    } else if (req) {
        const { headers } = req;
        if (headers) {
            const { 'user-agent': userAgent } = headers;
            if (userAgent) {
                return userAgent;
            }
        }
    }
    return EMPTY_STR;
}

function isClientUserAgentBot(req?: NextApiRequest): boolean {
    const ua = getUserAgent(req);
    return isUserAgentBot(ua);
}

function isSearchEngineBot(req: NextRequest | IncomingMessage): boolean {
    const ua = getHeaderValue(req, 'user-agent', EMPTY_STR);

    return isUserAgentBot(ua);
}

/**
 * A request is "language-agnostic" when it expresses no browser language preference:
 * the User-Agent is a known crawler, or it omits `Accept-Language`. Crawlers — notably
 * Googlebot — send a blank `Accept-Language` and crawl non-US region URLs from US IPs,
 * so region/locale for these requests must come from the URL, not the geo IP or browser
 * language. This is the shared signal used by both `getRegion` (URL region resolution)
 * and `getLocale` (region-default locale) so the two stay converged.
 */
function isLanguageAgnosticRequest(req: NextRequest | IncomingMessage): boolean {
    return (
        isSearchEngineBot(req) ||
        getHeaderValue(req, 'accept-language', EMPTY_STR).trim() === EMPTY_STR
    );
}

function isMobileUserAgent(req: NextRequest | IncomingMessage): boolean {
    const secChUaMobile = getHeaderValue(req, 'sec-ch-ua-mobile', EMPTY_STR);
    if (secChUaMobile === '?1') {
        return true;
    }

    const ua = getHeaderValue(req, 'user-agent', EMPTY_STR);
    return isMobilePhoneUserAgent(ua);
}

export {
    getUserAgent,
    getWebHost,
    isClientUserAgentBot,
    isLanguageAgnosticRequest,
    isMobileUserAgent,
    isSearchEngineBot,
};
